import * as React from 'react'
import { MenuRowShortcutKbd } from '@/components/ui/shortcut-kbd'

import {
  ContextMenuItem,
  ContextMenuSeparator,
  EditorContextMenuShell,
} from '../editor-context-menu-shell'

export interface CanvasBackgroundContextMenuProps {
  open: boolean
  position: { x: number; y: number }
  onOpenChange: (open: boolean) => void
  viewportBoundsRef?: React.RefObject<HTMLElement | null>
  onClose?: () => void
  onPaste?: (pos: { x: number; y: number }) => void
  onPasteInPlace?: () => void
  onSelectAll?: () => void
  onSelectNone?: () => void
  /** Shown only when the spread has locked shapes or text boxes. */
  onUnlockAll?: () => void
  showUnlockAll?: boolean
  /** Shown only when the spread has frozen shapes or text boxes. */
  onUnfreezeAll?: () => void
  showUnfreezeAll?: boolean
  /** Opens the Backgrounds panel (and with it the on-canvas background options). Shown only
   *  while that panel isn't already open. */
  onAdjustBackground?: () => void
  showAdjustBackground?: boolean
  disabledItems?: {
    paste?: boolean
    pasteInPlace?: boolean
    selectNone?: boolean
    unlockAll?: boolean
    unfreezeAll?: boolean
  }
}

export function CanvasBackgroundContextMenu({
  open,
  position,
  onOpenChange,
  viewportBoundsRef,
  onClose,
  onPaste,
  onPasteInPlace,
  onSelectAll,
  onSelectNone,
  onUnlockAll,
  showUnlockAll = false,
  onUnfreezeAll,
  showUnfreezeAll = false,
  onAdjustBackground,
  showAdjustBackground = false,
  disabledItems = {},
}: CanvasBackgroundContextMenuProps) {
  const handleItemClick = (handler?: () => void) => {
    handler?.()
    onOpenChange(false)
  }

  return (
    <EditorContextMenuShell
      open={open}
      position={position}
      onOpenChange={onOpenChange}
      viewportBoundsRef={viewportBoundsRef}
    >
      <ContextMenuItem onClick={() => handleItemClick(onClose)}>Close</ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem
        onClick={() => {
          onPaste?.(position)
          onOpenChange(false)
        }}
        disabled={disabledItems.paste}
      >
        Paste
        <MenuRowShortcutKbd shortcutKey="paste" />
      </ContextMenuItem>
      <ContextMenuItem
        onClick={() => handleItemClick(onPasteInPlace)}
        disabled={disabledItems.pasteInPlace}
      >
        Paste in place
        <MenuRowShortcutKbd shortcutKey="pasteInPlace" />
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem onClick={() => handleItemClick(onSelectAll)}>
        Select all
        <MenuRowShortcutKbd shortcutKey="selectAll" />
      </ContextMenuItem>
      <ContextMenuItem
        onClick={() => handleItemClick(onSelectNone)}
        disabled={disabledItems.selectNone}
      >
        Select none
        <MenuRowShortcutKbd shortcutKey="selectNone" />
      </ContextMenuItem>

      {showUnlockAll && onUnlockAll ? (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => handleItemClick(onUnlockAll)}
            disabled={disabledItems.unlockAll}
          >
            Unlock All
          </ContextMenuItem>
        </>
      ) : null}

      {showUnfreezeAll && onUnfreezeAll ? (
        <ContextMenuItem
          onClick={() => handleItemClick(onUnfreezeAll)}
          disabled={disabledItems.unfreezeAll}
        >
          Unfreeze All
        </ContextMenuItem>
      ) : null}

      {showAdjustBackground && onAdjustBackground ? (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => handleItemClick(onAdjustBackground)}>
            Adjust background
          </ContextMenuItem>
        </>
      ) : null}
    </EditorContextMenuShell>
  )
}
